import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { authApi } from '../../api';

const OtpContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9f9;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const OtpCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 564px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    gap: 20px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 590;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  margin: 0 0 4px 0;
  letter-spacing: 0.352px;
  width: 100%;
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  letter-spacing: -0.154px;
  width: 100%;
`;

const OtpInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 24px 0;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const OtpInput = styled.input`
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  background-color: #ffffff;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &.error {
    border-color: #cc3340;
  }
`;

const VerifyButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
  }
`;

const ResendButton = styled.button`
  background: transparent;
  color: #8d4f1b;
  border: none;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  
  &:hover {
    color: #703815;
  }
  
  &:disabled {
    color: #d8dcde;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const ErrorMessage = styled.div`
  color: #cc3340;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #00a86b;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: center;
`;

const BottomText = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  text-align: center;
  letter-spacing: -0.154px;
  margin: 0;
`;

const BackLink = styled.span`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: bold;
  color: #8d4f1b;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const VerifyPasswordResetOTP: React.FC = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
    
    setError('');
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Step 2: Verify reset token using the new two-step flow
      const response = await authApi.verifyResetToken(email, otpCode);
      console.log(response);
      
      if (response.tokenValid) {
        setSuccess('Reset token verified successfully! Redirecting to reset password...');
        
        // Redirect to reset password page with the verified token
        setTimeout(() => {
          navigate('/reset-password', {
            state: { 
              resetToken: otpCode, // Pass the verified token
              email: email
            } 
          });
        }, 1000);
      }
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await authApi.resendResetToken(email);
      setSuccess('Verification code sent successfully!');
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForgotPassword = () => {
    navigate('/forgot-password');
  };

  if (!email) {
    return null;
  }

  return (
    <OtpContainer>
      <OtpCard>
        <TitleSection>
          <Title>Verify Reset Code</Title>
          <Subtitle>
            We've sent a 6-digit reset code to {email}. Enter the code below to verify your identity and proceed to reset your password.
          </Subtitle>
        </TitleSection>
        
        <OtpInputContainer>
          {otp.map((digit, index) => (
            <OtpInput
              key={index}
              id={`otp-${index}`}
              type="text"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={error ? 'error' : ''}
              disabled={loading}
              maxLength={1}
            />
          ))}
        </OtpInputContainer>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <VerifyButton
          onClick={handleVerifyOTP}
          disabled={loading || otp.join('').length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify Reset Code'}
        </VerifyButton>
        
        <div style={{ textAlign: 'center' }}>
          <ResendButton
            onClick={handleResendOTP}
            disabled={loading || resendCooldown > 0}
          >
            {resendCooldown > 0 
              ? `Resend code in ${resendCooldown}s` 
              : 'Resend verification code'
            }
          </ResendButton>
        </div>
        
        <BottomText>
          Go back to <BackLink onClick={handleBackToForgotPassword}>Forgot Password</BackLink>
        </BottomText>
      </OtpCard>
    </OtpContainer>
  );
};

export default VerifyPasswordResetOTP;
